import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ExternalLink, Briefcase, MapPin, Building2, TrendingUp, FileText, Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'

interface Term {
  id: number
  name: string
  slug: string
  hierarchy?: string
}

interface Job {
  id: number
  title: string
  status: string
  content: string
  engagement: string | null
  featured_image: string | null
  job_description_thumbnail: string | null
  job_description_url: string | null
  banner_image: string | null
  experience_level: Term[]
  job_type: Term[]
  region: Term[]
  industries: Term[]
  created_at: string
  modified_at: string
}

export default function MyJobListingsPage() {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchJobs()
  }, [])

  const fetchJobs = async () => {
    try {
      setLoading(true)
      setError(null)

      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/jobs`
      const headers: Record<string, string> = {}
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to fetch jobs')
      }

      setJobs(result.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
      console.error('Error fetching jobs:', err)
    } finally {
      setLoading(false)
    }
  }

  const createNewJob = async () => {
    try {
      setCreating(true)
      setError(null)

      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/jobs`
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      }
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          title: 'New Job',
          status: 'draft',
          content: ''
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create job')
      }

      // Navigate to the newly created job
      if (result.data?.id) {
        navigate(`/jobs/${result.data.id}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job')
      console.error('Error creating job:', err)
    } finally {
      setCreating(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'publish':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  const getEngagementColor = (engagement: string | null) => {
    if (!engagement) return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    
    switch (engagement.toLowerCase()) {
      case 'hybrid':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'remote':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'temporary':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Spinner className="h-8 w-8" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Job Listings</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Browse and manage your job positions
            </p>
          </div>
          <Button 
            onClick={createNewJob} 
            disabled={creating}
            size="lg"
          >
            {creating ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create New Job
              </>
            )}
          </Button>
        </div>

        {jobs.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No jobs yet</h3>
                <p className="text-muted-foreground mb-4">
                  Get started by creating your first job posting.
                </p>
                <Button 
                  onClick={createNewJob} 
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <Spinner className="h-4 w-4 mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Job
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job) => (
              <Card 
                key={job.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <CardHeader>
                  {job.featured_image && (
                    <div className="mb-4 -mx-6 -mt-6">
                      <img 
                        src={job.featured_image} 
                        alt={job.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                    </div>
                  )}
                  <CardTitle className="flex items-start justify-between gap-2">
                    <span className="text-lg line-clamp-2">{job.title}</span>
                  </CardTitle>
                  <CardDescription className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <Badge className={getStatusColor(job.status)}>
                        {job.status === 'publish' ? 'Published' : 'Draft'}
                      </Badge>
                      {job.engagement && (
                        <Badge className={getEngagementColor(job.engagement)}>
                          {job.engagement}
                        </Badge>
                      )}
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Experience Level */}
                  {job.experience_level && job.experience_level.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium mb-2">
                        <TrendingUp className="h-4 w-4" />
                        <span>Experience Level</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {job.experience_level.map((level) => (
                          <Badge key={level.id} variant="outline" className="text-xs">
                            {level.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Job Type */}
                  {job.job_type && job.job_type.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium mb-2">
                        <Briefcase className="h-4 w-4" />
                        <span>Job Type</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {job.job_type.map((type) => (
                          <Badge key={type.id} variant="outline" className="text-xs">
                            {type.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Region */}
                  {job.region && job.region.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium mb-2">
                        <MapPin className="h-4 w-4" />
                        <span>Location</span>
                      </div>
                      <div className="space-y-1">
                        {job.region.map((region) => (
                          <div key={region.id} className="text-xs text-muted-foreground">
                            {region.hierarchy || region.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Industries */}
                  {job.industries && job.industries.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium mb-2">
                        <Building2 className="h-4 w-4" />
                        <span>Industries</span>
                      </div>
                      <div className="space-y-1">
                        {job.industries.map((industry) => (
                          <div key={industry.id} className="text-xs text-muted-foreground">
                            {industry.hierarchy || industry.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 space-y-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="w-full"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>{job.title}</DialogTitle>
                        </DialogHeader>
                        <ScrollArea className="h-[60vh] pr-4">
                          {job.banner_image && (
                            <div className="mb-6">
                              <img 
                                src={job.banner_image} 
                                alt="Job banner"
                                className="w-full h-64 object-cover rounded-lg"
                              />
                            </div>
                          )}
                          <div 
                            className="prose dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: job.content }}
                          />
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>

                    {job.job_description_url && (
                      <Button 
                        variant="default" 
                        className="w-full"
                        onClick={() => window.open(job.job_description_url!, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Job Description
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
